import { Fragment, useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { ButtonHeight } from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { MenuTabs } from '~/components/ui/MenuTabs';
import { PanelControls } from '~/components/ui/PanelControls';
import { ShopContainer } from '~/components/ui/Shop';
import { ShopBalance } from '~/components/ui/ShopBalance';
import { ShopPreview } from '~/components/ui/ShopPreview';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, InputKey, WalletType } from '~/shared';
import { ChangeTab } from '../../components/ChangeTab';
import { plateTypeList } from '../../components/ChangeTab/ChangeTab.model';
import { Head } from '../../components/Head';
import { RegionSelect } from '../../components/RegionSelect';
import { StorageTab } from '../../components/StorageTab';
import { VehicleInformation } from '../../components/VehicleInformation';
import { useBuy, useShopPreview } from '../../hooks';
import {
	resetStore,
	setNumber,
	setNumberInput,
	setRegion,
	setRegionCode,
	setSelectedTypeIndex,
	setTabIndex,
	setTabIndexNext,
	setTabIndexPrev,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledControlsContainer,
	StyledMarginContainer,
	StyledRightContainer,
} from './PlateBuying.styled';

const tabs = [ChangeTab, StorageTab];

const tabItems = ['Смена номера', 'Хранилище'];

const PlateBuying: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const tabIndex = useLayoutSelector((state) => state.plateBuying.tabIndex);
	const numberPlate = useObservable(
		rage.vehicleNumberPlates.getLocalOwnedVehicleNumberPlate(),
	);
	const regionList = useObservable(rage.vehicleShop.getNumberPlateRegions());
	const currentModal = useObservable(rage.modal.getCurrentModal());
	const regionSelectVisible = useLayoutSelector(
		(state) => state.plateBuying.regionSelectVisible,
	);
	const shopPreview = useShopPreview();
	const { handleBuy } = useBuy();

	const ActiveTab = tabs[tabIndex];

	const leftControls = useMemo(() => {
		const changeButtons =
			tabIndex === 0
				? [
						{
							text: 'Сменить тип номера',
							keys: [InputKey.ArrowLeft, InputKey.ArrowRight],
						},
						{
							text: 'Оплатить номер',
							keys: [InputKey.Enter],
							onClick: () => handleBuy(WalletType.Cash),
						},
				  ]
				: [];

		return changeButtons;
	}, [tabIndex, handleBuy]);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => rage.vehicleNumberPlates.exitMenu(),
		},
	];

	useInputAction(InputAction.CommonMenuClose, () => {
		if (currentModal) {
			rage.modal.replyToCurrentModal({
				isAccepted: false,
			});
		} else {
			rage.vehicleNumberPlates
				.exitMenu()
				.catch((err) => rage.notifications.showException(err));
		}
	});

	useEffectOnce(() => {
		dispatch(
			setRegion(
				regionList.find((region) => region.code === numberPlate?.region),
			),
		);
		dispatch(setRegionCode(numberPlate?.region ?? ''));
		dispatch(setNumber(numberPlate?.text ?? ''));
		dispatch(setNumberInput(numberPlate?.text ?? ''));
		const index = plateTypeList.findIndex(
			(type) => type.type === numberPlate?.type,
		);
		dispatch(setSelectedTypeIndex(index));

		return () => {
			rage.vehicleNumberPlates
				.resetPreviewNumberPlate()
				.catch((err) => rage.notifications.showException(err));
			dispatch(resetStore());
		};
	});

	useEffect(() => {
		if (tabIndex === 0) {
			return;
		}

		rage.vehicleNumberPlates
			.resetPreviewNumberPlate()
			.catch((err) => rage.notifications.showException(err));
	}, [tabIndex]);

	return (
		<Fragment>
			<StyledRightContainer>
				<ShopBalance />
				<CloseButton
					height={ButtonHeight.Height40}
					iconSize={20}
					onClick={() => rage.vehicleNumberPlates.exitMenu()}
				/>
			</StyledRightContainer>
			<ShopContainer>
				<Head />
				{regionSelectVisible ? (
					<RegionSelect />
				) : (
					<Fragment>
						<MenuTabs
							tabs={tabItems}
							activeIndex={tabIndex}
							handlePrev={() => dispatch(setTabIndexPrev())}
							handleNext={() => dispatch(setTabIndexNext())}
							setIndex={(index) => dispatch(setTabIndex(index))}
							style={{ marginTop: 20 }}
						/>
						<StyledMarginContainer>
							<VehicleInformation />
						</StyledMarginContainer>
						<ActiveTab />
					</Fragment>
				)}
			</ShopContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align={'left'} />
				<PanelControls controls={rightControls} align={'right'} />
			</StyledControlsContainer>
			<ShopPreview {...shopPreview} />
		</Fragment>
	);
};

export { PlateBuying };
